import { createSelector } from 'reselect'
import { apiAxios } from '@/api'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { keyBy, sortBy } from 'lodash'
import { DateTime } from 'luxon'

import { fetchIfNeededWrapper, fetchWrapper } from './fetch'
import { ReduxState } from '../utils/typeHelpers'
import {
  fetchStripePlans,
  HEARD_LITE_PRODUCT_IDS,
  STARTER_PLAN_PRODUCT_IDS,
} from '../constants/pricingConstants'
import { StripeProductMetadata } from '@/actions/settings/billingActions'
import { getBillingPeriod } from '@/utils/stripeSubscriptionHelpers'

export const SUBSCRIPTION_DEFAULT_KEY = 'default'

interface SubscriptionInvoice {
  id: string
  invoiceUrl: string
  amountPaid: number
  created: number
}

interface StripeDiscount {
  id: string
}

export interface SubscriptionState {
  [key: string]: {
    id: string
    name: string
    startDate: number
    currentBillingStartDate: number | null
    nextBillingDate: number | null
    productId: string
    productName: string | null
    isPrimary: boolean
    /** False for a legacy product: one the catalog provisioner did not create. */
    isCatalogProduct: boolean
    priceId: string
    items: {
      [key: string]: {
        id: string
        name: string
        amountInCents: number
        quantity: number
        interval: StripeInterval
      }
    }
    invoices: {
      [key: string]: SubscriptionInvoice
    }
    discounts: Array<string | StripeDiscount>
    status: string
  }
}

const initialState: SubscriptionState = {}

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    receiveSubscriptions: (
      _state,
      action: PayloadAction<SubscriptionResponse>
    ) => {
      const next: SubscriptionState = {}
      for (const sub of action.payload.subscriptions) {
        const period = getBillingPeriod(sub.subscription, sub.priceId)
        next[sub.product.name] = {
          id: sub.subscription.id,
          name: sub.product.name,
          startDate: sub.subscription.start_date,
          currentBillingStartDate: period.start,
          nextBillingDate: period.end,
          productId: sub.product.id,
          productName: sub.product.metadata.displayName ?? null,
          isPrimary: sub.product.metadata['heard-product-type'] === 'primary',
          isCatalogProduct:
            sub.product.metadata['heard-product-key'] !== undefined,
          priceId: sub.priceId,
          items: keyBy(
            sub.subscription.items.data.map((item) => ({
              id: item.id,
              amountInCents: item.plan.amount,
              quantity: item.quantity,
              name: item.plan.nickname || SUBSCRIPTION_DEFAULT_KEY,
              interval: item.plan.interval,
            })),
            (item) => item.name
          ),
          invoices: keyBy(
            sub.invoices.data.map((item) => ({
              id: item.id,
              invoiceUrl: item.hosted_invoice_url,
              amountPaid: item.amount_paid,
              created: item.created,
            })),
            (item) => item.id
          ),
          discounts: sub.subscription.discounts,
          status: sub.subscription.status,
        }
      }
      return next
    },
  },
})

export type StripeSubscription = {
  id: string
  items: {
    data: Array<{
      id: string
      plan: {
        id: string
        amount: number
        nickname: string | null
        interval: StripeInterval
      }
      quantity: number
      price?: { id: string }
      current_period_start?: number
      current_period_end?: number
    }>
  }
  plan: {
    /** This is the price Id */
    id: string
    product: string
  } | null
  discounts: Array<string | StripeDiscount>
  start_date: number
  current_period_end?: number
  current_period_start?: number
  cancel_at: number | null
  cancel_at_period_end: boolean
  status: string
  primaryPriceId?: string | null
}

export enum StripeInterval {
  month = 'month',
  year = 'year',
  week = 'week',
  day = 'day',
}

// A ton of data comes back with this so it is not fully typed
interface SubscriptionResponse {
  subscriptions: Array<{
    subscription: StripeSubscription
    product: {
      id: string
      name: string
      metadata: StripeProductMetadata
    }
    priceId: string
    invoices: {
      data: Array<{
        id: string
        hosted_invoice_url: string
        amount_paid: number
        created: number
      }>
    }
  }>
}

export default subscriptionSlice.reducer

const { receiveSubscriptions } = subscriptionSlice.actions

export const FETCH_SUBSCRIPTIONS_KEY = 'FETCH_SUBSCRIPTIONS_KEY'
export const fetchSubscriptions = () =>
  fetchIfNeededWrapper({
    fetchKey: FETCH_SUBSCRIPTIONS_KEY,
    fetchFunction: async (dispatch) => {
      const json = await apiAxios.get<SubscriptionResponse>(
        '/finances/api/v1/stripe/subscriptions'
      )

      dispatch(receiveSubscriptions(json.data))
    },
  })

export interface InvoiceResponse {
  id: string
  hosted_invoice_url: string
  amount_paid: number
  amount_due: number
  created: number
  status?: 'paid' | 'open' | 'void' | 'uncollectible' | 'draft' | null
}

export const fetchInvoices = (params: { starting_after_invoice_id?: string }) =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await apiAxios.get<{
        data: InvoiceResponse[]
        has_more: boolean
      }>('/finances/api/v1/stripe/invoices', { params })

      return json.data
    },
  })

const HEARD_PRODUCT_BASE_PLAN_NAME = 'default'

const HEARD_PAYROLL_SUB_NAME = 'Heard Payroll'
const HEARD_PAYROLL_PER_PERSON_NAME = 'Per Seat Pricing'
const HEARD_PAYROLL_BASE_PLAN_NAME = 'Base Package Fee'

export const selectSubscription = (state: ReduxState) => state.subscription
export const selectAllInvoices = createSelector(selectSubscription, (subs) =>
  sortBy(
    Object.values(subs).flatMap((sub) => Object.values(sub.invoices)),
    'created'
  )
)

const stripePlans = fetchStripePlans()

export const selectHeardProductSubscription = createSelector(
  selectSubscription,
  (subs) => Object.values(subs).find((sub) => sub.isPrimary)
)

export const selectIsOnLegacyProduct = createSelector(
  selectHeardProductSubscription,
  (sub) => (sub ? !sub.isCatalogProduct : false)
)

/**
 * Selects the name of the plan if available, otherwise defaults to 'Basic'
 */
export const selectPlanNameOrBasic = createSelector(
  selectHeardProductSubscription,
  (sub) => {
    return sub?.productName || 'Basic'
  }
)

export const selectHeardPayrollSubscription = createSelector(
  selectSubscription,
  (subs) => (subs[HEARD_PAYROLL_SUB_NAME] ? subs[HEARD_PAYROLL_SUB_NAME] : null)
)
const selectGepPerPersonSub = createSelector(
  selectHeardPayrollSubscription,
  (sub) =>
    sub?.items[HEARD_PAYROLL_PER_PERSON_NAME]
      ? sub.items[HEARD_PAYROLL_PER_PERSON_NAME]
      : null
)

export const selectGepBasePrice = createSelector(
  selectHeardPayrollSubscription,
  (sub) =>
    sub?.items[HEARD_PAYROLL_BASE_PLAN_NAME]
      ? sub.items[HEARD_PAYROLL_BASE_PLAN_NAME].amountInCents
      : null
)

export const selectGepPerPersonPrice = createSelector(
  selectGepPerPersonSub,
  (sub) => sub?.amountInCents
)

export const selectHeardProductPlanPrice = createSelector(
  selectHeardProductSubscription,
  (sub) =>
    sub?.items[HEARD_PRODUCT_BASE_PLAN_NAME]?.amountInCents ??
    Object.values(sub?.items ?? {})[0]?.amountInCents ??
    null
)

export const selectHeardProductPlanInterval = createSelector(
  selectHeardProductSubscription,
  (sub) =>
    sub?.items[HEARD_PRODUCT_BASE_PLAN_NAME]?.interval ??
    Object.values(sub?.items ?? {})[0]?.interval ??
    null
)

/**
 * This function computes the days until a customer's account is suspended. In Stripe, a
 * subscription is marked `past_due` if an auto-renewing subscription fails to charge the
 * specified payment method (for us it'll be the user's default payment method) when auto
 * renewing the subscription. As configured in our console, Stripe will automatically attempt
 * to renew the subscription and charge the payment method for 14 days, after which the
 * subscription will be marked as `unpaid`, and we will suspend the user's access to the core
 * features of Heard (minus messaging).
 */
export const selectDaysUntilSuspension = createSelector(
  selectSubscription,
  (subscription) => {
    const subscriptions = Object.values(subscription)

    // notify the caller if there is already an unpaid subscription
    if (subscriptions.find((sub) => sub.status === 'unpaid')) {
      return -1
    }

    // filter subscriptions by `past_due` status and sort by the start date of the current billing
    // period
    const pastDueSubscriptions = subscriptions
      .filter(
        (sub) =>
          sub.status === 'past_due' && sub.currentBillingStartDate !== null
      )
      .sort(
        (sub1, sub2) =>
          (sub1.currentBillingStartDate ?? 0) -
          (sub2.currentBillingStartDate ?? 0)
      )

    // the first subscription item in the collection will be the earliest subscription that was
    // marked `past_due` and will eventually be the first subscription that will be marked `unpaid`
    const earliestPastDueSubscriptionTimestamp =
      pastDueSubscriptions[0]?.currentBillingStartDate ?? -1

    // subtract the number of days since that earliest subscription became `past_due` and subtract
    // that difference in days from 14 to get the number of days remaining before the account is
    // officially suspended
    return earliestPastDueSubscriptionTimestamp !== -1
      ? 14 -
          Math.trunc(
            DateTime.now().diff(
              DateTime.fromSeconds(earliestPastDueSubscriptionTimestamp),
              'days'
            ).days
          )
      : -1
  }
)

export const selectProductIds = createSelector(
  selectSubscription,
  (subscription) => Object.values(subscription).map((s) => s.productId)
)

export const selectPriceIds = createSelector(
  selectSubscription,
  (subscription) => Object.values(subscription).map((s) => s.priceId)
)

export const selectHasPrimarySubscription = createSelector(
  selectSubscription,
  (subs) => Object.values(subs).some((sub) => sub.isPrimary)
)

export const selectIsBasicPlan = createSelector(
  selectProductIds,
  (stripeProductIds) =>
    stripeProductIds.includes(stripePlans.basic.productId || '') ||
    stripeProductIds.includes(stripePlans.basic_group.productId || '') ||
    stripeProductIds.includes(stripePlans.basic_solo.productId || '') ||
    stripeProductIds.includes(stripePlans.basic_no_qte.productId || '') ||
    stripeProductIds.some((id) => STARTER_PLAN_PRODUCT_IDS.includes(id)) ||
    stripeProductIds.some((id) => HEARD_LITE_PRODUCT_IDS.includes(id))
)

export const selectIsStarterPlan = createSelector(
  selectProductIds,
  (stripeProductIds) =>
    stripeProductIds.some((id) => STARTER_PLAN_PRODUCT_IDS.includes(id))
)

export const selectIsHeardLitePlan = createSelector(
  selectProductIds,
  (stripeProductIds) =>
    stripeProductIds.some((id) => HEARD_LITE_PRODUCT_IDS.includes(id))
)
